import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { CreateMeetingSlotAction } from "../../Redux/Actions/OrganizationAction";

interface CreateMeetingSlotModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DEFAULT_TITLE = "SkillBrew Product Overview";
const DEFAULT_DESCRIPTION = `SkillBrew.AI is a suite of software tools that provides upskilling, hiring, and placement solutions to learners, organizations, and universities.

A Learner
- AI-tailored personalized learning
- Step-by-step guide with lab assessments
- Learning Specialised Roadmaps
- Mock AI Interviews

An Organisation
- E2E automated hiring plans
- Import bulk candidates
- Manage Coding, MCQ or Behavioural Assessments
- Proctoring Suite with full coverage
- AI Interviews
- Assessment and Interview Insights and detailed reporting

Universities
- Complete Training and Placement Management.
- 360-degree coverage of software development course
- Student SkillProfiles for placement drive
- Archive the placement information over the years
- Say goodbye to the manual Excel and WhatsApp groups
- Discussion forums for students
- Connect with Alumni`;

const CreateMeetingSlotModal: React.FC<CreateMeetingSlotModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: DEFAULT_TITLE,
    start_datetime: "",
    end_datetime: "",
    description: DEFAULT_DESCRIPTION,
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleChange = (e: any) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getDaysDifference = () => {
    if (!formData?.start_datetime || !formData?.end_datetime) return 0;
    const start = new Date(formData?.start_datetime)?.getTime();
    const end = new Date(formData?.end_datetime)?.getTime();
    return (end - start) / (1000 * 60 * 60 * 24);
  };

  const diffDays = getDaysDifference();
  const isDateError = formData?.end_datetime !== "" && diffDays > 5;
  const isDateNegative = formData?.end_datetime !== "" && diffDays <= 0;

  const handleSubmit = async () => {
    if (isDateError || isDateNegative) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        title: formData?.title,
        start_datetime: new Date(formData?.start_datetime)?.toISOString()?.split('.')[0] + 'Z',
        end_datetime: new Date(formData?.end_datetime)?.toISOString()?.split('.')[0] + 'Z',
        description: formData?.description,
      };

      const result = await dispatch(CreateMeetingSlotAction(payload));
      
      if (result && result?.status === true) {
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess(); 
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: DEFAULT_TITLE,
      start_datetime: "",
      end_datetime: "",
      description: DEFAULT_DESCRIPTION,
    });
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getMaxEndDate = () => {
    if (!formData?.start_datetime) return undefined;
    const date = new Date(formData?.start_datetime);
    date?.setDate(date?.getDate() + 5);
    const offset = date?.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(date?.getTime() - offset))?.toISOString()?.slice(0, 16);
    return localISOTime;
  };

  const getMinEndDate = () => {
    if (!formData?.start_datetime) return undefined;
    const date = new Date(formData?.start_datetime);
    const offset = date?.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(date?.getTime() - offset))?.toISOString()?.slice(0, 16);
    return localISOTime;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Meeting Slot</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            name="title"
            label="Title"
            value={formData?.title}
            onChange={handleChange}
            fullWidth
            required
            placeholder="e.g., SkillBrew Product Overview"
          />

          <TextField
            name="start_datetime"
            label="Start Date & Time"
            type="datetime-local"
            value={formData?.start_datetime}
            onChange={handleChange}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            name="end_datetime"
            label="End Date & Time"
            type="datetime-local"
            value={formData?.end_datetime}
            onChange={handleChange}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: getMinEndDate(),
              max: getMaxEndDate(),
            }}
            error={isDateError || isDateNegative}
            helperText={
              isDateError 
                ? "Maximum 5 days duration allowed." 
                : isDateNegative && formData?.end_datetime !== ""
                  ? "End date must be after start date." 
                  : "You can select up to 5 days maximum."
            }
          />

          <TextField
            name="description"
            label="Description"
            value={formData?.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            placeholder="Describe the meeting slot"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData?.title ||
            !formData?.start_datetime ||
            !formData?.end_datetime ||
            isDateError ||
            isDateNegative
          }
          sx={{
            backgroundColor: "#0555c8",
            "&:hover": {
              backgroundColor: "#043c99",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Create Slot"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMeetingSlotModal;
